import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom"; // Import useParams and Link
import moment from 'moment';

// reactstrap components
import {
  Button, // Added Button for potential back link
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  CardText,
  ListGroup,
  ListGroupItem,
  Spinner,
  Alert
} from "reactstrap";

// core components
import IndexNavbar from "components/Navbars/IndexNavbar.js";
// Using ProfilePageHeader, but you might want a more generic one
import ProfilePageHeader from "components/Headers/ProfilePageHeader.js";
import IndexFooter from "components/Footers/IndexFooter.js";

// API endpoint for fetching a specific vendor (adjust if needed)
const API_VENDOR_URL = "https://api.pembrokefarmersmarket.ca/api/vendors";

function PublicVendorProfilePage() {
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams(); // Get vendor ID from URL parameter
  const navigate = useNavigate(); // For back button

  // Data Fetching Logic
  useEffect(() => {
    const fetchVendor = async () => {
      if (!id) {
        setError("Vendor ID not provided.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Fetch public data for a specific vendor
        const response = await axios.get(`${API_VENDOR_URL}/${id}`);
        // Ensure attendees is an array
         const vendor = {
            ...response.data,
            attendees: Array.isArray(response.data.attendees) ? response.data.attendees : []
        };
        setVendorData(vendor);

      } catch (err) {
        console.error("Error fetching vendor data:", err.response || err);
        setError("Could not load vendor information. The vendor may not exist or there was a server error.");
      } finally {
        setLoading(false);
      }
    };

    fetchVendor();
  }, [id]); // Re-fetch if ID changes

  // Add/remove body class
  useEffect(() => {
    document.body.classList.add("profile-page"); // Reusing profile-page class
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    return function cleanup() {
      document.body.classList.remove("profile-page");
    };
  }, []);

  // Helper to format dates
  const formatDate = (dateString) => {
     const options = { year: 'numeric', month: 'long', day: 'numeric' };
     try {
       if (!dateString || !moment(dateString).isValid()) return "Invalid Date";
       return new Date(dateString).toLocaleDateString(undefined, options);
     } catch (e) {
       return "Invalid Date";
     }
   };

  return (
    <>
      <IndexNavbar />
      {/* Header can be customized */}
      <ProfilePageHeader />
      <br />
      <div className="main">
        <div className="section profile-content">
          <Container>
            {/* Back Button */}
             <Row>
                <Col className="mb-3">
                    <Button
                        className="btn-round"
                        color="primary"
                        outline
                        onClick={() => navigate(-1)} // Go back to previous page
                    >
                        <i className="nc-icon nc-minimal-left mr-1"></i>
                        Back to Vendors
                    </Button>
                </Col>
             </Row>

            {/* Loading State */}
            {loading && (
              <Row>
                <Col className="text-center"><Spinner color="primary" /> Loading Vendor...</Col>
              </Row>
            )}

            {/* Error State */}
            {error && !loading && (
              <Row>
                <Col><Alert color="danger">{error}</Alert></Col>
              </Row>
            )}

            {/* Vendor Data Display */}
            {!loading && !error && vendorData && (
              <Row>
                {/* Use one column or adjust layout as needed */}
                <Col md="10" lg="8" className="mx-auto"> {/* Centered column */}
                   <Card>
                     <CardHeader>
                       {/* Display Business Name, fallback to Owner Name if needed */}
                       <CardTitle tag="h3">{vendorData.businessName || vendorData.name || 'Vendor Profile'}</CardTitle>
                       {/* Display Vendor Type */}
                       <p className="text-muted mb-0">{vendorData.type || 'Vendor'}</p>
                    </CardHeader>
                    <CardBody>
                       {/* Display Description */}
                       <h5 className="card-category">About</h5>
                       <CardText className="description mb-3">
                           {vendorData.description || 'No description provided.'}
                       </CardText>
                       <hr/>

                       {/* Display Market Days */}
                       <h5 className="card-category">Market Schedule</h5>
                       <CardText className="mb-3">
                           {vendorData.marketDays || 'Please check calendar or contact vendor for schedule.'}
                       </CardText>
                       <hr/>

                       {/* Display Attending Events */}
                       <h5 className="card-category">Attending Special Events</h5>
                       {/* Check vendorData.attendingEvents instead of attendees */}
                       {vendorData.attendingEvents && vendorData.attendingEvents.length > 0 ? (
                         <ListGroup flush className="mb-0">
                           {/* Map over vendorData.attendingEvents */}
                           {vendorData.attendingEvents.map(event => (
                             <ListGroupItem key={event._id} className="px-0 py-1"> {/* Use event._id as key */}
                               {/* Display event name and formatted date */}
                               {event.name ? `${event.name} - ${formatDate(event.date)}` : `Event (ID: ${event._id})`}
                             </ListGroupItem>
                           ))}
                         </ListGroup>
                       ) : (
                         <p className="text-muted mb-0">Not currently signed up for special events.</p>
                       )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
             {/* Message if loading finished but no data/error */}
             {!loading && !error && !vendorData && (
                <Row><Col><p className="text-center text-muted">Vendor data not found.</p></Col></Row>
             )}
          </Container>
        </div>
      </div>
      <IndexFooter />
    </>
  );
}

export default PublicVendorProfilePage;